import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { useMemo, useState } from 'react'
import type { RouterInputs, RouterOutputs } from 'shared/api'
import { trpc, trpcClient } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { Hint, Stat, Stats } from 'shared/components/section'
import { ToneNote } from 'shared/components/tone-banner'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from 'shared/components/ui/dialog'
import { useDataTable } from 'shared/hooks/use-data-table'
import { useTranslate } from 'shared/lib/i18n'
import { cn } from 'shared/lib/utils'
import { formatDayTime } from 'shared/lib/utils/date'
import { actsAsAdmin } from 'shared/lib/roles'
import { useAuthStore } from 'shared/store/auth'
import { MONEY_COLUMN, som, sum, WHOLE } from 'shared/lib/money'

type Difference = RouterOutputs['cashBags']['Differences'][number]
export type DecideDifferenceRequest = RouterInputs['cashBags']['DecideDifference']
export type DecideDifferenceAnswer = RouterOutputs['cashBags']['DecideDifference']

/** A row's key in test ids: a bag may differ twice, at the packing and at the central cashbox. */
const keyOf = (d: Difference) => `${d.code}-${d.arose}`

/**
 * «Разницы сумок» at the central cashbox (ADR-0036, ADR-0047; user story 112;
 * ticket 57): every Cash bag whose count differed, with the comment — at the
 * packing, against the operator; at the central cashbox, against whomever the
 * admin decides: the operator who packed it or the driver who carried it. Until
 * then the row says «на кого — решает админ», and the admin has «Решить» on it,
 * which asks in a dialog. A difference is TezGo's, never the partner's.
 */
export function CashBagDifferences() {
	const queryClient = useQueryClient()
	const { t } = useTranslate()
	const isAdmin = actsAsAdmin(useAuthStore((s) => s.user.role))
	const query = useQuery(trpc.cashBags.Differences.queryOptions())
	const decide = useMutation({
		mutationFn: (input: DecideDifferenceRequest) => trpcClient.cashBags.DecideDifference.mutate(input),
		onSettled: () => queryClient.invalidateQueries(),
	})
	return (
		<div className='flex flex-col gap-6'>
			{decide.data?.outcome === 'refused' && (
				<ToneNote
					tone='refused'
					title={t(`cashBagDecideRefusal.${decide.data.reason}`)}
					testId='cash-bag-decide-refused'
				/>
			)}
			<CashBagDifferenceList
				items={query.data}
				loading={query.isFetching && !query.data}
				decide={isAdmin ? (input) => decide.mutate(input) : undefined}
				deciding={decide.isPending}
			/>
		</div>
	)
}

export function CashBagDifferenceList({
	items,
	loading = false,
	decide,
	deciding = false,
}: {
	items?: Difference[]
	loading?: boolean
	/** The admin's decision — absent for anyone else, who only reads. */
	decide?: (input: DecideDifferenceRequest) => void
	deciding?: boolean
}) {
	const { t } = useTranslate()
	const [asking, setAsking] = useState<Difference | null>(null)
	const data = useMemo(() => items ?? [], [items])
	const undecided = data.filter((d) => !d.against).length
	const shortage = data.reduce((total, d) => total + Math.max(0, -d.difference), 0)
	const surplus = data.reduce((total, d) => total + Math.max(0, d.difference), 0)

	const columns = useMemo<ColumnDef<Difference>[]>(
		() => [
			{
				id: 'at',
				header: t('cashier.colAt'),
				cell: ({ row }) => <span className='tabular-nums'>{formatDayTime(row.original.at)}</span>,
			},
			{
				id: 'point',
				header: t('cashier.colPoint'),
				cell: ({ row }) => <span className='font-semibold'>{row.original.pointCode}</span>,
			},
			{
				id: 'arose',
				header: t('cashier.colArose'),
				cell: ({ row }) => (
					<Badge variant='outline' className='font-normal'>
						{t(row.original.arose === 'packing' ? 'cashier.arosePacking' : 'cashier.aroseReceipt')}
					</Badge>
				),
			},
			{
				id: 'comment',
				header: t('cashier.comment'),
				cell: ({ row }) => row.original.comment ?? <span className='text-muted-foreground'>—</span>,
			},
			{
				id: 'against',
				header: () => (
					<span className='inline-flex items-center gap-1.5'>
						{t('cashier.colAgainst')}
						<Hint>{t('cashier.differencesExplain')}</Hint>
					</span>
				),
				cell: ({ row: { original: d } }) => (
					<span
						className={cn(!d.against && 'text-amber-700 dark:text-amber-400')}
						data-testid={`cash-bag-difference-against-${keyOf(d)}`}
					>
						{d.against
							? t(d.against.role === 'operator' ? 'cashier.againstOperator' : 'cashier.againstDriver', {
									name: d.against.name,
								})
							: t('cashier.againstUndecided')}
					</span>
				),
			},
			{
				id: 'difference',
				header: t('cashier.colDifference'),
				meta: MONEY_COLUMN,
				cell: ({ row: { original: d } }) => (
					<span
						className={cn('font-semibold', d.difference < 0 && 'text-destructive')}
						data-testid={`cash-bag-difference-amount-${keyOf(d)}`}
					>
						{d.difference > 0 ? `+${sum(d.difference)}` : sum(d.difference)}
					</span>
				),
			},
			...(decide
				? [
						{
							id: 'actions',
							header: '',
							meta: { cellClassName: 'w-px text-right' },
							cell: ({ row: { original: d } }) =>
								!d.against && (
									<Button
										size='sm'
										variant='outline'
										data-testid={`cash-bag-decide-${keyOf(d)}`}
										disabled={deciding}
										onClick={() => setAsking(d)}
									>
										{t('cashier.decide')}
									</Button>
								),
						} satisfies ColumnDef<Difference>,
					]
				: []),
		],
		[t, decide, deciding],
	)
	const { table } = useDataTable({
		data,
		columns,
		rowCount: data.length,
		...WHOLE,
		getRowId: (d) => `${d.cashBagId}-${d.arose}`,
	})

	return (
		<div className='flex flex-col gap-6'>
			<Stats testId='cash-bag-differences-totals'>
				<Stat label={t('cashier.undecided')} value={String(undecided)} tone='warn' />
				<Stat label={t('cashier.shortageTotal')} value={sum(shortage)} tone='bad' />
				<Stat label={t('cashier.surplusTotal')} value={sum(surplus)} />
			</Stats>
			<DataTable
				table={table}
				withPagination={false}
				emptyText={
					loading ? (
						t('money.loading')
					) : (
						<span data-testid='cash-bag-differences-empty'>{t('cashier.differencesEmpty')}</span>
					)
				}
				row={{ attributes: (d) => ({ 'data-testid': `cash-bag-difference-${keyOf(d)}` }) }}
			/>

			<Dialog open={asking !== null} onOpenChange={(open) => !open && setAsking(null)}>
				{asking && decide && (
					<DialogContent className='sm:max-w-md' data-testid='cash-bag-decide-dialog'>
						<DialogHeader>
							<DialogTitle>{t('cashier.decideTitle')}</DialogTitle>
							<DialogDescription>
								{t('cashier.decideWhat', {
									point: asking.pointCode,
									date: formatDayTime(asking.at),
									difference: t(asking.difference < 0 ? 'cashier.shortage' : 'cashier.surplus', {
										amount: som(t, Math.abs(asking.difference)),
									}),
								})}
							</DialogDescription>
						</DialogHeader>
						{asking.comment && <p className='text-sm'>«{asking.comment}»</p>}
						<p className='text-sm text-muted-foreground'>{t('cashier.decideOnce')}</p>
						<DialogFooter>
							<DialogClose asChild>
								<Button variant='ghost'>{t('cashier.cancel')}</Button>
							</DialogClose>
							<Button
								variant='outline'
								data-testid={`cash-bag-decide-operator-${keyOf(asking)}`}
								disabled={deciding}
								onClick={() => {
									decide({ cashBagId: asking.cashBagId, against: 'operator' })
									setAsking(null)
								}}
							>
								{t('cashier.decideOperator', { name: asking.operator.name })}
							</Button>
							{asking.driver && (
								<Button
									variant='outline'
									data-testid={`cash-bag-decide-driver-${keyOf(asking)}`}
									disabled={deciding}
									onClick={() => {
										decide({ cashBagId: asking.cashBagId, against: 'driver' })
										setAsking(null)
									}}
								>
									{t('cashier.decideDriver', { name: asking.driver.name })}
								</Button>
							)}
						</DialogFooter>
					</DialogContent>
				)}
			</Dialog>
		</div>
	)
}
